/**
 * The store behind the sync indicator (`112-offline-drafts.md` §10,
 * `110-design-system.md` §9).
 *
 * `useSyncExternalStore` over the outbox's own `subscribe()` — the outbox is the source
 * of truth for "did my words land", and it lives in IndexedDB, not in React.
 *
 * **The user must never have to wonder whether their work landed.** The indicator is the
 * answer to that question, and it is on screen at all times.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { getConflicts, subscribeConflicts, type Conflict } from '@/offline/conflicts';
import { getSyncState, subscribe, type SyncState } from '@/offline/outbox';

export function useSyncStatus(): SyncState {
  return useSyncExternalStore(subscribe, getSyncState, getSyncState);
}

/** The recovery banner for one page, if it has one. */
export function useConflict(pageId: string | undefined): Conflict | undefined {
  const get = useCallback(
    () => (pageId === undefined ? undefined : getConflicts().get(pageId)),
    [pageId],
  );

  return useSyncExternalStore(subscribeConflicts, get, get);
}

export function useConflictCount(): number {
  return useSyncExternalStore(
    subscribeConflicts,
    () => getConflicts().size,
    () => getConflicts().size,
  );
}
